import React, { useMemo, useState } from 'react';
import { Alert, Button, Flex, Form, Input, Table } from 'antd';
import useOngoingOrder from '../../hooks/context/useOngoingOrder';
import { ICustomer } from '../../types/customer';
import { loadCustomerDetails, searchCustomers } from '../../services/api/CustomerService';
import useStore from '../../hooks/context/useStore';
import useForceRender from '../../hooks/context/useForceRender';
import { ReservedKeys } from '../../providers/ForceRenderProvider';
import { useNavigate } from 'react-router';
import { Routes } from '../../routing/Router';
import OrderFlowStepper from '../../components/pages/order-flow/OrderFlowStepper';
import useCustomerDetails from '../../hooks/context/useCustomerDetails';
import PhoneNumberInput from '../../components/form/PhoneNumberInput';

export default function NewOrderPage() {
  const { storeId } = useStore();
  const { setCustomerData } = useCustomerDetails();
  const { orderData, updateOrderData, resetCustomerInfo } = useOngoingOrder();

  const { forceRender } = useForceRender();
  const navigate = useNavigate();

  const [searchResult, setSearchResult] = useState<ICustomer[]>(null);
  const [searching, setSearching] = useState(false);
  const [loadingCustomerDetails, setLoadingCustomerDetails] = useState(null);

  const isInputValid = useMemo(
    () =>
      storeId && Boolean(orderData.customer.phone_number) && Boolean(orderData.customer.first_name),
    [storeId, orderData.customer],
  );

  const loadCustomers = () => {
    setSearching(true);
    searchCustomers(storeId, { phone_number: orderData.customer.phone_number })
      .then(({ data }) => setSearchResult(data))
      .catch((error) => console.error('Failed to search customers', { error }))
      .finally(() => setSearching(false));
  };

  const onSelectCustomer = (customer: ICustomer) => {
    setLoadingCustomerDetails(customer.phone_number);
    loadCustomerDetails(storeId, { phone_number: customer.phone_number })
      .then(({ data }) => {
        setCustomerData(data);
        updateOrderData({
          customer: customer,
          invoice_info: {
            company_name: customer.invoice_company_name,
            tax_id: customer.invoice_tax_id,
          },
          delivery_info: {
            ...customer.delivery_info,
            zone: null,
            quote: null,
          },
        });
      })
      .then(() => navigate(`/${Routes.NewOrder.Root}/${Routes.NewOrder.OrderItems}`))
      .finally(() => setLoadingCustomerDetails(null));
  };

  const onFormReset = () => {
    resetCustomerInfo();
    forceRender(ReservedKeys.CustomerPhoneNumber);
  };

  return (
    <>
      <OrderFlowStepper className="mt-2xl" />
      <h1>Customer lookup</h1>
      {!storeId && (
        <Alert
          message="Select a store to proceed"
          description="Select a store from sidebar or wait for incoming call to be able to create the order"
          className="mt-sm"
          showIcon
        />
      )}
      <Form layout="vertical" className="mt-md">
        <div className="grid grid-cols-2 gap-lg gap-y-0">
          <Form.Item label="Phone number" required>
            <PhoneNumberInput
              distinct
              country="de"
              enableSearch
              preferredCountries={['de']}
              forwardKey={ReservedKeys.CustomerPhoneNumber}
              value={orderData.customer.phone_number}
              onChange={(value: string) => updateOrderData('customer.phone_number', value)}
            />
          </Form.Item>
          <Form.Item label="First name" required>
            <Input
              value={orderData.customer.first_name}
              onChange={(e) => updateOrderData('customer.first_name', e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Last name">
            <Input
              value={orderData.customer.last_name}
              onChange={(e) => updateOrderData('customer.last_name', e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Email">
            <Input
              value={orderData.customer.email}
              onChange={(e) => updateOrderData('customer.email', e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Company name" className="col-span-2 mb-none">
            <Input
              value={orderData.invoice_info.company_name}
              onChange={(e) => updateOrderData('invoice_info.company_name', e.target.value)}
            />
          </Form.Item>
        </div>
      </Form>
      <Flex className="mt-xl" justify="space-between">
        <Button
          type="primary"
          size="large"
          disabled={!isInputValid}
          onClick={() => navigate(`/${Routes.NewOrder.Root}/${Routes.NewOrder.OrderItems}`)}
        >
          Add customer and start order
        </Button>
        <div className="flex gap-md">
          <Button size="large" onClick={loadCustomers} loading={searching}>
            Search
          </Button>
          <Button type="text" size="large" onClick={onFormReset}>
            Clear
          </Button>
        </div>
      </Flex>
      {searchResult && (
        <Table
          columns={[
            {
              key: 'actions',
              title: 'Actions',
              render: (item) => (
                <>
                  <Button
                    loading={loadingCustomerDetails === item.phone_number}
                    onClick={() => onSelectCustomer(item)}
                  >
                    Start order
                  </Button>
                </>
              ),
            },
            {
              key: 'name',
              title: 'Name',
              render: (item) => `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim(),
            },
            {
              key: 'phone_number',
              title: 'Phone',
              dataIndex: 'phone_number',
            },
            {
              key: 'email',
              title: 'Email',
              dataIndex: 'email',
            },
            {
              key: 'company_name',
              title: 'Company name',
              dataIndex: 'company_name',
            },
          ]}
          dataSource={searchResult}
          rowKey="phone_number"
          pagination={false}
          className="mt-xl overflow-x-auto"
        />
      )}
    </>
  );
}
